import { Progress } from "../../../../../domains/DrillProgress";
import { ContentItem } from "../../../../../features/SectionsTimelinePage/StudyRecordAnswerDetailDrawer/ContentItem";
import { useFetchStudyRecordContentsWithAnswers } from "../../../../../features/SectionsTimelinePage/useFetchStudyRecordContentsWithAnswers";
import StudentInterface from "../../../../../interfaces/StudentInterface";
import type { StudyRecordWithRestrictedAttributes } from "../../../../../interfaces/StudyRecordInterface";
import Icon from "../../../../atoms/Icon";
import Loader from "../../../../atoms/Loader";
import { TimelineDrillProgressBar } from "../DrillProgressBar";
import { Record } from "./Record";
import { RestrictedContent } from "./RestrictedContent";
import { StudyProgressLink } from "./StudyProgressLink";
import styles from "./styles.scss";

type Props = {
  studyRecord: StudyRecordWithRestrictedAttributes;
  student: StudentInterface;
  sectionId: string;
  showHeader?: boolean;
  drillProgressMap?: Map<string, Progress>;
  isShowDrillAnswer?: boolean;
};

export const Content = ({
  studyRecord,
  sectionId,
  drillProgressMap,
  isShowDrillAnswer = false,
}: Props) => {
  // NOTE: 現状の仕様では学習量と学習時間を個別に制限することはないので、&&にしている
  const isRestrictedContent =
    studyRecord.restrictedAttributes.includes("numberOfSeconds") &&
    studyRecord.restrictedAttributes.includes("amount");

  // Notee 教材はドリル基盤に載るため isTypeDrill は true になるが、タイムラインでは
  // 通常教材として表示する（定着度バー・回答詳細ドロワーは出さない）。そのため
  // notee フラグで除外し、ドリル扱いするのは「ドリルかつ Notee でない」場合のみとする。
  const isDrillMaterial =
    !!studyRecord.learningMaterial?.isTypeDrill &&
    !studyRecord.learningMaterial?.notee;

  // ドリル教材の場合のみ回答詳細を取得
  const {
    data: answerDetailData,
    isLoading: isLoadingAnswerDetail,
    isError: isAnswerDetailError,
  } = useFetchStudyRecordContentsWithAnswers({
    sectionId,
    studyRecordId: studyRecord.id,
    enabled: isDrillMaterial && !!isShowDrillAnswer,
  });

  const hasAnswerDetail =
    answerDetailData?.drillContentsWithAnswers &&
    answerDetailData.drillContentsWithAnswers.length > 0;

  // エラー時またはデータなしの場合はローディングを表示しない
  const showAnswerDetailLoader = isLoadingAnswerDetail && !isAnswerDetailError;

  return (
    <div className={styles.content}>
      {/* 1. 教材画像 */}
      <div className="flex w-full">
        <div>
          {studyRecord.learningMaterial &&
          studyRecord.learningMaterial.imageUrl ? (
            <div className={styles.imageContainer}>
              <div
                className={styles.image}
                style={{
                  backgroundImage: `url(${studyRecord.learningMaterial.imageUrl})`,
                }}
              />
            </div>
          ) : (
            <div className={styles.imageContainer}>
              <div className={styles.image} />
            </div>
          )}
        </div>
        <div className={styles.right}>
          {/* 教材名 */}
          <div className={styles.learningMaterial}>
            <div className={styles.title} data-testid="timeline-item-title">
              {studyRecord.learningMaterial
                ? studyRecord.learningMaterial.name
                : "教材なし"}
            </div>
            <div className="flex justify-start flex-col ml-1">
              {studyRecord.externalLink && (
                <a
                  href={studyRecord.externalLink ?? ""}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={styles.externalLink}
                >
                  詳細を確認
                  <Icon name="icon-external-link" />
                </a>
              )}
            </div>
          </div>
          {/* 学習時間・学習量 */}
          <div className="flex gap-4">
            {isRestrictedContent ? (
              <RestrictedContent studyRecord={studyRecord} />
            ) : (
              <Record studyRecord={studyRecord} />
            )}
          </div>
          {/* デジタル教材定着度バー（Notee 教材は除外） */}
          {studyRecord.learningMaterial && isDrillMaterial && (
            <div className="flex flex-col md:flex-row md:items-start mt-4 md:gap-4">
              <div className="py-2">
                <StudyProgressLink
                  sectionId={sectionId}
                  learningMaterialCode={
                    studyRecord.learningMaterial.learningMaterialCode ?? ""
                  }
                />
              </div>
              {drillProgressMap?.get(studyRecord.id) && (
                <div className="flex-grow md:pl-6 md:pr-8 py-3 md:py-0">
                  <TimelineDrillProgressBar
                    progress={drillProgressMap?.get(studyRecord.id)}
                    invisible={!drillProgressMap?.get(studyRecord.id)}
                    rounded
                    showTotalCount
                    bold
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* ドリル教材の回答詳細（全幅レイアウト） */}
      {isDrillMaterial && isShowDrillAnswer && (
        <div className="w-full mt-6">
          {showAnswerDetailLoader ? (
            <Loader loading />
          ) : answerDetailData ? (
            <div className="flex flex-col gap-5">
              {/* セクション名 */}
              {answerDetailData.drillSection?.name && (
                <div className="rounded bg-gray-100 p-4 flex items-center justify-between">
                  <span className="text-base leading-[18px] text-gray-900 flex-1 pr-[10px]">
                    {answerDetailData.drillSection.name}
                  </span>
                </div>
              )}
              {/* コンテンツ一覧 */}
              {hasAnswerDetail && (
                <div className="flex flex-col">
                  {answerDetailData.drillContentsWithAnswers?.map(
                    (content, index) => (
                      <div key={content.numberWithinSection ?? index}>
                        <ContentItem content={content} contentIndex={index} />
                        {index <
                          (answerDetailData.drillContentsWithAnswers?.length ??
                            0) -
                            1 && (
                          <div className="my-[3.2rem] h-px w-full bg-gray-400" />
                        )}
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
